import { useContext } from "react";
import { useInterviewRoundContext } from "../../Context/InterviewRoundContext";
import LoaderContext from "../../Context/LoaderContext/Index";
import { DashboardContext } from "../../Context/DashboardContext";
import AgentService from "../../Services/agent/agent.service";
import messages from "../../Services/agent/messages";
import InterviewService from "../../Services/interview.service";
import { useNavigate } from "react-router-dom";
import { JobApplication } from "../../Entities/job-application.entity";
import usePollingFunctions from "./usePollingFunctions";
import { InterviewSession } from "../../Interface/Index";
import { openNotification } from "../../Components/Notification/Index";
import { RadioChangeEvent } from "antd";

interface InterviewRoundsHandlersReturnType {
  handleSubmitStep: () => void;
  handleEvaluation: () => Promise<void>;
  toggleToFullscreenMode: () => void;
  toggleToNormalScreenMode: () => void;
  handleCameraAccessConfirmation: () => Promise<void>;
  handleCancelCameraAccessConfirmation: () => void;
  handleCameraAccessdenied: () => void;
  handleRetryEvaluation: () => Promise<void>;
  handleDisqualify: () => Promise<void>;
  handleCancelExit: () => void;
  handleConfirmExit: () => void;
  handleAnswerSingleQuestion: (index: number) => Promise<void>;
  handleBack: () => void;
  handleNext: () => Promise<void>;
  handleStartInterview: () => void;
  handleQuestionAnswerChange: (
    e: React.ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent,
    value?: string | null
  ) => void;
  onNavigateToQuestions: () => void;
}

const useInterviewRoundsHandlers = (): InterviewRoundsHandlersReturnType => {
  const navigate = useNavigate();

  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const agentService = AgentService.getInstance();
  const interviewService = new InterviewService();

  const {
    appliedJob,
    checkLastRound,
    interviewSessions,
    enableRounds,
    enabledInterviewRoundTabs,
    addSession,
    jobDescription,
    setAppliedJob,
    setShowResultTab,
    setShowOnlineInterviewTab,
  } = useContext(DashboardContext);

  const {
    setActiveQuestionIndex,
    currentSessionQuestions,
    isRoundFinished,
    currentSession,
    setShowRetryEvaluation,
    setCurrentSession,
    setShowCamRecorder,
    isFullscreen,
    elementRef,
    setIsRoundFinished,
    activeQuestionIndex,
    setShowCameraConfirmation,
    setShowExitConfirmation,
    setExitTimer,
    setCurrentSessionQuestionAnswers,
    currentSessionQuestionAnswers,
    setCurrentSessionQuestions,
  } = useInterviewRoundContext();

  // Polling functions
  const {
    checkForNewSessions,
    checkForNextRoundQuestions,
    checkApplicationStatus,
  } = usePollingFunctions();

  const toggleToFullscreenMode = () => {
    if (!isFullscreen) {
      if (elementRef.current) {
        if (elementRef.current.requestFullscreen) {
          elementRef.current.requestFullscreen();
        } else if (elementRef.current.mozRequestFullScreen) {
          elementRef.current.mozRequestFullScreen(); // Firefox
        } else if (elementRef.current.webkitRequestFullscreen) {
          elementRef.current.webkitRequestFullscreen(); // Chrome, Safari, Opera
        } else if (elementRef.current.msRequestFullscreen) {
          elementRef.current.msRequestFullscreen(); // IE/Edge
        } else {
          console.error("Fullscreen API is not supported on this element.");
        }
      } else {
        console.error("Element reference is null.");
      }
    }
  };

  const toggleToNormalScreenMode = () => {
    if (isFullscreen && document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  const handleQualifiedRound = async (appliedJob: JobApplication) => {
    try {
      setLoadingMessage("Preparing next round...");
      agentService.setCurrentMessage(messages.PREPARING_NEXT_ROUND, "", "");

      const newSessions = await checkForNewSessions(
        appliedJob?._id,
        interviewSessions
      );

      if (!newSessions || newSessions.length === 0) {
        console.error("No new interview sessions found.");
        setLoading(false);
        setLoadingMessage(null);
        return;
      }

      setLoadingMessage("Generating questionnaire...");
      agentService.setCurrentMessage(messages.GENERATING_QUESTIONNAIRE, "", "");

      const nextRoundSession = newSessions.find(
        (session) => session.status === "pending"
      );

      if (!nextRoundSession) {
        console.error("No pending session found.");
        setLoading(false);
        setLoadingMessage(null);
        return;
      }

      const questions = await checkForNextRoundQuestions(nextRoundSession._id);

      if (questions?.length) {
        const roundName = enabledInterviewRoundTabs.find(
          (round) => round.roundId === nextRoundSession.interviewRoundId
        )?.roundName;
        console.log("🚀 ~ handleQualifiedRound ~ roundName:", roundName);

        if (roundName) {
          enableRounds(nextRoundSession.interviewRoundId, nextRoundSession._id);
          addSession(nextRoundSession);
          setLoading(false);
          setLoadingMessage(null);
          navigate(
            `/${jobDescription?._id}/interview-round/${nextRoundSession._id}`
          );
        }
      } else {
        console.warn("No questions found for the next round.");
      }
    } catch (error) {
      console.error("Error handling qualified round:", error);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleUnqualifiedRound = async (
    appliedJob: JobApplication,
    session: InterviewSession
  ) => {
    agentService.setCurrentMessage(messages.EVALUATION_UNQUALIFIED, "", "");
    session.status = "unqualified";
    setCurrentSession(session);
    setIsRoundFinished(true);
    setActiveQuestionIndex(currentSessionQuestions.length);
    let updatedApplication = await checkApplicationStatus(
      appliedJob._id,
      "rejected"
    );
    if (!updatedApplication) {
      console.error("No updated application found");
      return;
    }
    setAppliedJob(updatedApplication);
    setShowResultTab(true);
    setLoading(false);
    setLoadingMessage(null);
    navigate(`/${jobDescription?._id}/result`);
  };

  const handleLastRoundQualified = async (
    appliedJob: JobApplication,
    session: InterviewSession
  ) => {
    if (!jobDescription) {
      console.error("No job description found");
      return;
    }
    try {
      const isOnlineInterviewEnabled = jobDescription.onlineInterview;
      if (isOnlineInterviewEnabled) {
        setLoadingMessage("Setting up online interview...");
        agentService.setCurrentMessage(messages.EVALUATION_QUALIFIED, "", "");
        const updatedApplication = await checkApplicationStatus(
          appliedJob._id,
          "interview"
        );
        if (!updatedApplication) {
          console.error("No updated application found");
          return;
        }
        setAppliedJob(updatedApplication);
        setIsRoundFinished(true);
        setShowOnlineInterviewTab(true);
        agentService.setCurrentMessage(
          messages.WAITING_FOR_INTERVIEW_SCHEDULE,
          "",
          "Select Slot"
        );
        navigate(`/${jobDescription?._id}/online-interview`);
      } else {
        const updatedApplication = await checkApplicationStatus(
          appliedJob._id,
          "shortlisted"
        );
        if (!updatedApplication) {
          console.error("No updated application found");
          return;
        }
        setAppliedJob(updatedApplication);
        session.status = "qualified";
        setCurrentSession(session);
        setIsRoundFinished(true);
        setShowResultTab(true);
        agentService.setCurrentMessage(messages.ALL_ROUND_CLEARED, "", "");
        setLoading(false);
        setLoadingMessage(null);
        navigate(`/${jobDescription?._id}/result`);
      }
    } catch (error) {
      console.error("Error handling last round qualified:", error);
      setLoading(false);
      setLoadingMessage(null);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleEvaluation = async () => {
    if (isRoundFinished) {
      console.warn("handleEvaluation: Round is already finished");
      return;
    }

    if (!currentSession || !appliedJob) {
      console.log("handleEvaluation: No session found");
      return;
    }
    setLoading(true);
    setLoadingMessage("Evaluating your answers please wait...");

    try {
      agentService.setCurrentMessage(
        messages.WAITING_FOR_EVALUATION_RESULT,
        "",
        ""
      );

      const evaluationResult = await interviewService.finishExam(
        currentSession._id
      );

      if (!evaluationResult) {
        console.error("handleEvaluation: No evaluation result found");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        return;
      }

      if (!evaluationResult.success) {
        console.error("handleEvaluation: workflow failed");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        return;
      }

      const result = evaluationResult.status;
      if (!result) {
        console.error("handleEvaluation: Failed to finish the exam");
        setLoading(false);
        setLoadingMessage(null);
        setShowRetryEvaluation(true);
        return;
      }

      toggleToNormalScreenMode();

      // Handling evaluation status
      if (result === "qualified") {
        currentSession.status = "qualified";
        setCurrentSession(currentSession);
        const isLastRound = checkLastRound(currentSession._id);
        setShowCamRecorder(false);
        if (isLastRound) {
          return await handleLastRoundQualified(appliedJob, currentSession);
        }
        return await handleQualifiedRound(appliedJob);
      } else if (result === "unqualified") {
        return await handleUnqualifiedRound(appliedJob, currentSession);
      }
    } catch (error) {
      console.error("Failed to finish the exam", error);
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage(null);
      }, 1000);
      setShowRetryEvaluation(true);
    }
  };

  const handleSubmitStep = () => {
    setActiveQuestionIndex(currentSessionQuestions.length);
  };

  const handleDisqualify = async () => {
    handleSubmitStep();
    await handleEvaluation();
  };

  const handleCameraAccessConfirmation = async () => {
    try {
      setShowCameraConfirmation(false);

      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });

      if (permissionStatus.state === "denied") {
        openNotification(
          "error",
          "Camera access blocked",
          "You have blocked camera access. Please enable camera permission from your browser settings and refresh the page."
        );
        setShowCamRecorder(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      const delay = permissionStatus.state === "prompt" ? 300 : 0;

      setTimeout(() => {
        setShowCamRecorder(true);
        toggleToFullscreenMode();
        setActiveQuestionIndex(0);
      }, delay);

      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      console.error("Camera access error:", err);
      setShowCamRecorder(false);
      openNotification(
        "error",
        "Camera access error",
        "Unable to access camera. Please check your browser settings and allow access."
      );
    }
  };

  const handleCancelCameraAccessConfirmation = () => {
    setShowCameraConfirmation(false);
    openNotification(
      "info",
      "Camera access denied",
      "Without camera access, you will not be able to proceed with the interview. Please allow camera access to continue."
    );
  };

  const handleCameraAccessdenied = () => {
    if (activeQuestionIndex > -1) {
      toggleToNormalScreenMode();
      setActiveQuestionIndex(-1);
      setShowCamRecorder(false);
      setShowCameraConfirmation(false);
      openNotification(
        "info",
        "Camera access denied",
        "Without camera access, you will not be able to proceed with the interview."
      );
    }
  };

  const handleRetryEvaluation = async () => {
    setLoading(true);
    setShowRetryEvaluation(false);
    try {
      return await handleEvaluation();
    } catch (error) {
      console.error("Error during retry evaluation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setExitTimer(false);
    const isFullScreenActive =
      document.fullscreenElement !== elementRef?.current;

    if (elementRef?.current && isFullScreenActive) {
      elementRef.current.requestFullscreen().catch((err: any) => {
        console.error("Failed to re-enter fullscreen:", err);
      });
    }
  };

  const handleConfirmExit = () => {
    handleDisqualify();
    setShowExitConfirmation(false);
    setExitTimer(false);
  };

  const handleAnswerSingleQuestion = async (index: number) => {
    // If clicking on the same question, do nothing
    if (index === activeQuestionIndex) {
      return;
    }

    // Prevent going back to instructions once round has started or completed
    if (activeQuestionIndex > -1 && index === -1) {
      return;
    }

    // If clicking on a previous question, just navigate
    if (index < activeQuestionIndex) {
      setActiveQuestionIndex(index);
      return;
    }

    const currentQuestion = currentSessionQuestions[activeQuestionIndex];

    if (
      !currentQuestion &&
      activeQuestionIndex !== currentSessionQuestions.length
    ) {
      console.log("No question found", activeQuestionIndex);
      return;
    }

    if (!currentSession) {
      console.error("No session found");
      return;
    }

    // If round is finished, allow navigation to any question
    if (isRoundFinished) {
      setActiveQuestionIndex(index);
      return;
    }

    // If current question is already answered, allow navigation
    const isAnswered =
      currentQuestion?.responses && currentQuestion.responses.length > 0;
    if (isAnswered) {
      setActiveQuestionIndex(index);
      return;
    }

    // Check if current question has an answer
    const questionType = currentQuestion?.type;
    const answer =
      questionType === "fileUpload"
        ? currentSessionQuestionAnswers[activeQuestionIndex]?.fileUrl
        : currentSessionQuestionAnswers[activeQuestionIndex]?.answer;

    // If there's no answer, just navigate to the next question
    if (!answer) {
      setActiveQuestionIndex(index);
      return;
    }

    // If there is an answer, submit it before navigating
    setLoading(true);
    setLoadingMessage("Submitting answer please wait...");

    try {
      const updatedQuestion = await interviewService.answerSingleQuestion(
        answer,
        currentSession._id,
        currentQuestion._id
      );

      if (updatedQuestion) {
        setActiveQuestionIndex(index);
        setCurrentSessionQuestions((prev) => {
          const newQuestions = [...prev];
          newQuestions[activeQuestionIndex] = updatedQuestion;
          return newQuestions;
        });

        setCurrentSessionQuestionAnswers((prev) => {
          const newAnswers = [...prev];
          newAnswers[activeQuestionIndex] = {
            questionId: updatedQuestion._id,
            answer: questionType !== "fileUpload" ? answer : null,
            fileUrl: questionType === "fileUpload" ? answer : null,
          };
          return newAnswers;
        });
      } else {
        console.error("Failed to answer question");
        openNotification(
          "error",
          "Failed to answer question",
          "Please try again"
        );
        setActiveQuestionIndex(index); // Still navigate even if submission fails
      }
    } catch (error) {
      console.error("Error handling next question", error);
      setActiveQuestionIndex(index); // Still navigate even if there's an error
    } finally {
      setTimeout(() => {
        setLoading(false);
        setLoadingMessage(null);
      }, 1000);
    }
  };

  const handleBack = () => {
    // Prevent going back to instructions once round has started or completed
    if (activeQuestionIndex > -1) {
      if (activeQuestionIndex > 0) {
        setActiveQuestionIndex(activeQuestionIndex - 1);
      }
    }
  };

  const handleNext = async () => {
    const nextIndex = activeQuestionIndex + 1;
    if (nextIndex <= currentSessionQuestions.length) {
      await handleAnswerSingleQuestion(nextIndex);
    }
  };

  const handleStartInterview = () => {
    if (activeQuestionIndex === -1) {
      setShowCameraConfirmation(true);
    } else {
      setActiveQuestionIndex(-1);
    }
  };

  const handleQuestionAnswerChange = (
    e: React.ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent,
    value?: string | null
  ) => {
    if (isRoundFinished) {
      console.warn("handleQuestionAnswer: Round is already finished");
      return;
    }

    const currentQuestion = currentSessionQuestions[activeQuestionIndex];
    if (!currentQuestion) {
      console.error("handleQuestionAnswer: No question found");
      return;
    }
    const currentQuestionType = currentQuestion.type;
    const answer = currentQuestionType === "coding" ? value : e.target.value;
    const isSameAnswer =
      answer === currentSessionQuestionAnswers[activeQuestionIndex]?.answer;

    if (e.target) {
      setCurrentSessionQuestionAnswers((prev) => {
        const newAnswers = [...prev];
        if (currentQuestionType === "fileUpload") {
          newAnswers[activeQuestionIndex] = {
            ...newAnswers[activeQuestionIndex],
            fileUrl: answer,
          };
        } else {
          if (isSameAnswer) {
            newAnswers[activeQuestionIndex] = {
              ...newAnswers[activeQuestionIndex],
              answer: null,
            };
          } else {
            newAnswers[activeQuestionIndex] = {
              ...newAnswers[activeQuestionIndex],
              answer,
            };
          }
        }
        return newAnswers;
      });
    } else if (value) {
      setCurrentSessionQuestionAnswers((prev) => {
        const newAnswers = [...prev];
        newAnswers[activeQuestionIndex] = {
          ...newAnswers[activeQuestionIndex],
          answer: value,
        };
        return newAnswers;
      });
    } else if (currentQuestionType === "coding" && !value) {
      setCurrentSessionQuestionAnswers((prev) => {
        const newAnswers = [...prev];
        newAnswers[activeQuestionIndex] = {
          ...newAnswers[activeQuestionIndex],
          answer: null,
        };
        return newAnswers;
      });
    }
  };

  const onNavigateToQuestions = () => {
    setActiveQuestionIndex(currentSessionQuestions.length - 1);
  };

  return {
    handleSubmitStep,
    handleEvaluation,
    toggleToFullscreenMode,
    toggleToNormalScreenMode,
    handleCameraAccessConfirmation,
    handleCancelCameraAccessConfirmation,
    handleCameraAccessdenied,
    handleRetryEvaluation,
    handleDisqualify,
    handleCancelExit,
    handleConfirmExit,
    handleAnswerSingleQuestion,
    handleBack,
    handleNext,
    handleQuestionAnswerChange,
    handleStartInterview,
    onNavigateToQuestions,
  };
};

export default useInterviewRoundsHandlers;
